import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";

const BASE = `${import.meta.env.VITE_API_URL}/admin/hot-runs`;

/** What a unit of engine work cost. Mirrors BlackBox.Domain.Engine.Cost — the same nine numbers at every level. */
export interface Cost {
  /** Request units. Zero for a run entirely on table storage, where `storeRequests` is the cost line instead. */
  requestCharge: number;
  storeRequests: number;
  blobBytes: number;
  brokerSends: number;
  /** Requests that left for a vendor. One transaction can make several. */
  vendorCalls: number;
  /** Elapsed and not CPU — what was really waited through, the vendor's own time included. */
  computeMs: number;
  throttledVendorMs: number;
  throttledInternalMs: number;
  throttledOrgMs: number;
  throttledPlatformMs: number;
}

/** One run with work in flight. */
export interface HotRun {
  orgId: string;
  workflowRunId: string;
  /** How many of this run's services are being processed at once. */
  openServices: number;
  /** Which hosts hold them. Empty means every open service is between claims. */
  hosts: string[];
  /** What the open services have spent. The ranking number, and what this run is costing now. */
  live: Cost;
  /** How long the open services have been worked on, summed over their deliveries. */
  processingMs: number;
  /** What this run's completed services already cost, off the run's own document. */
  final: Cost;
  // Absent when the run can no longer be read — ordinary here, because a service completing deletes its
  // progress a moment after the scan.
  workflowId?: string | null;
  workflowName?: string | null;
  projectId?: string | null;
  runStatus?: string | null;
  runStartedAt?: string | null;
}

export interface HotRunsView {
  runs: HotRun[];
  /** Every service in flight, not only the ones behind the rows shown. */
  openServices: number;
  /** The scan hit its ceiling, so these are the costliest of what was read rather than of what exists. */
  scanCapped: boolean;
}

/** Row limit the server applies when the caller names none — mirrored so the view can say it capped. */
export const HOT_RUNS_DEFAULT_TAKE = 20;

/**
 * What the engine is spending on right now, most expensive first. Superadmin only — the endpoint group
 * carries the policy and the handler refuses anyway.
 *
 * Refetched on a timer because this is a live picture: a ranked list of in-flight work that does not move
 * reads as a stalled platform, which is the one thing it must not say by accident.
 */
export function useHotRuns(orgId: Ref<string | undefined>, take: Ref<number>) {
  const url = computed(() => {
    const params = new URLSearchParams();
    if (orgId.value) params.set("orgId", orgId.value);
    params.set("take", String(take.value));
    return `${BASE}?${params.toString()}`;
  });

  const { data, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: computed(() => ["admin-hot-runs", url.value]),
    queryFn: () => fetchWrapper.get<HotRunsView>(url.value),
    refetchInterval: 15_000,
  });

  return {
    hotRuns: computed(() => data.value?.runs ?? []),
    openServices: computed(() => data.value?.openServices ?? 0),
    scanCapped: computed(() => data.value?.scanCapped ?? false),
    hotRunsLoading: isFetching,
    hotRunsError: error as Ref<ApiError | null>,
    refetchHotRuns: refetch,
    readAt: dataUpdatedAt,
  };
}
